import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { toast } from 'dashboard/helper/toast';
import CallPermissionsAPI from 'dashboard/api/whatsappSip/callPermissions';

const PERMISSION_WAIT_MS = 60000; // 60s

// Normalize a wa_id/phone to digits-only.
const toDigits = value => String(value || '').replace(/\D/g, '');

// Match two wa_ids by their last 8+ digits (handles different normalization
// formats between the click context and the ActionCable event payload).
const sameContact = (a, b) => {
  const da = toDigits(a);
  const db = toDigits(b);
  if (!da || !db) return false;
  const matchLen = Math.min(8, da.length, db.length);
  return da.slice(-matchLen) === db.slice(-matchLen);
};

/**
 * Encapsulates the shared WhatsApp call-permission flow (check → request →
 * wait → dial) used by both the conversation button and the SIP dialer panel.
 *
 * State machine: idle | checking | requesting | waiting | permitted | denied
 */
export function useWhatsAppCallPermission() {
  const { t } = useI18n();
  const store = useStore();

  const state = ref('idle');
  let waitingTimeout = null;

  // Context of the in-flight / last request. The permissionChanged watcher
  // needs these to auto-dial on ACCEPT while the agent is waiting.
  let currentWaId = null;
  let currentOnPermitted = null;

  const permissionChanged = computed(
    () => store.getters['whatsappSipAutodialer/getPermissionChanged']
  );

  const isProcessing = computed(() =>
    ['checking', 'requesting'].includes(state.value)
  );

  const clearWaitingTimeout = () => {
    if (waitingTimeout) {
      clearTimeout(waitingTimeout);
      waitingTimeout = null;
    }
  };

  const startWaiting = () => {
    state.value = 'waiting';
    clearWaitingTimeout();
    waitingTimeout = setTimeout(() => {
      if (state.value === 'waiting') {
        state.value = 'idle';
        toast.error(t('WHATSAPP_SIP_CALL.PERMISSION.TIMEOUT'));
      }
    }, PERMISSION_WAIT_MS);
  };

  // ActionCable: the contact answered the permission request.
  watch(permissionChanged, val => {
    if (!val || !currentWaId || state.value === 'permitted') return;
    if (!sameContact(val.waId, currentWaId)) return;

    const wasWaiting = state.value === 'waiting';
    clearWaitingTimeout();

    const response = String(val.response || '').toUpperCase();
    if (response === 'ACCEPT') {
      state.value = 'permitted';
      if (wasWaiting) {
        toast.success(t('WHATSAPP_SIP_CALL.PERMISSION.GRANTED_DIALING'));
        currentOnPermitted?.();
      }
    } else {
      state.value = 'denied';
      toast.error(t('WHATSAPP_SIP_CALL.PERMISSION.DENIED'));
    }
  });

  async function requestCall({ waId, inboxId, onPermitted }) {
    currentWaId = waId;
    currentOnPermitted = onPermitted;

    // Already granted for this contact — dial straight away.
    if (state.value === 'permitted') {
      onPermitted?.();
      return;
    }

    // Already waiting on the contact's answer.
    if (state.value === 'waiting') {
      toast.info(t('WHATSAPP_SIP_CALL.PERMISSION.WAITING'));
      return;
    }

    // Guard against double clicks while a request is in flight.
    if (isProcessing.value) return;

    state.value = 'checking';
    try {
      const { data } = await CallPermissionsAPI.check(waId, inboxId);

      if (data.can_start_call) {
        state.value = 'permitted';
        onPermitted?.();
        return;
      }

      if (data.can_request_perm) {
        state.value = 'requesting';
        // Manual loading toast + toast.error so the failure renders the custom
        // ErrorToast (toast.promise's error path uses the plain sonner toast).
        const loadingId = toast.loading(
          t('WHATSAPP_SIP_CALL.PERMISSION.REQUESTING_LOADING')
        );
        try {
          await CallPermissionsAPI.requestPermission(waId, undefined, inboxId);
          toast.dismiss(loadingId);
          toast.success(t('WHATSAPP_SIP_CALL.PERMISSION.REQUEST_SENT'));
          startWaiting();
        } catch (err) {
          toast.dismiss(loadingId);
          toast.error(
            err?.response?.data?.error ||
              t('WHATSAPP_SIP_CALL.PERMISSION.CHECK_ERROR')
          );
          state.value = 'idle';
        }
        return;
      }

      state.value = 'denied';
      if (data.status === 'denied') {
        toast.error(t('WHATSAPP_SIP_CALL.PERMISSION.DENIED'));
      } else {
        toast.error(
          t('WHATSAPP_SIP_CALL.PERMISSION.LIMIT_REACHED', {
            used: data.perm_usage,
            limit: data.perm_limit,
          })
        );
      }
    } catch (err) {
      state.value = 'idle';
      toast.error(
        err?.response?.data?.error ||
          t('WHATSAPP_SIP_CALL.PERMISSION.CHECK_ERROR')
      );
    }
  }

  function reset() {
    state.value = 'idle';
    clearWaitingTimeout();
    currentWaId = null;
    currentOnPermitted = null;
  }

  onUnmounted(() => {
    clearWaitingTimeout();
  });

  return { state, requestCall, reset, isProcessing };
}
